import Api from '../../../../core/services/api/api';
import {CROSS_SALES_ENRICH_CONCURRENCY} from '../constants';
import type {CrossSalesPartApiItem, EnrichedCrossSalePart, SuggestiveSellingTarget} from '../types';
import {mapCrossSaleToEnrichedPart} from './map-to-lookup-card-props';

async function mapWithConcurrency<T, R>(
    items: T[],
    mapper: (item: T) => Promise<R>,
    concurrency: number,
): Promise<R[]> {
    const results: R[] = [];
    for (let i = 0; i < items.length; i += concurrency) {
        const chunk = items.slice(i, i + concurrency);
        const chunkResults = await Promise.all(chunk.map(mapper));
        results.push(...chunkResults);
    }
    return results;
}

/**
 * Enriches cross-sale API rows with part key information and optional customer pricing.
 */
export async function enrichCrossSaleParts(crossSales: CrossSalesPartApiItem[], target: SuggestiveSellingTarget): Promise<EnrichedCrossSalePart[]> {
    if (!crossSales.length) {
        return [];
    }
    const enriched = await mapWithConcurrency(
        crossSales,
        async (crossSale) => {
            const keyInfoResponse = await Api.getProductKeyInformation(crossSale.partNumber);
            const keyInfo = keyInfoResponse?.resultFlag ? keyInfoResponse.data : null;
            let part = mapCrossSaleToEnrichedPart(crossSale, keyInfo as { id?: number; partNo?: string; title?: string });
            if (target.type !== 'quickOrder' && target.customerId && keyInfo) {
                try {
                    const priceResponse = await Api.getPriceForCustomer({
                        customerId: target.customerId,
                        partNumbers: [crossSale.partNumber],
                    });
                    const priceEntry = priceResponse?.resultFlag
                        ? priceResponse.data?.find?.((e: { partNo?: string }) => e.partNo === crossSale.partNumber)
                        ?? priceResponse.data?.[0]
                        : undefined;
                    if (priceEntry) {
                        part = {
                            ...part,
                            priceInfo: {
                                newPrice: priceEntry.price,
                                originalPrice: priceEntry.originalPrice ?? priceEntry.price,
                            },
                        };
                    }
                } catch {
                    // Pricing is optional for suggestive cards
                }
            }
            return part;
        },
        CROSS_SALES_ENRICH_CONCURRENCY,
    );

    return enriched.filter((e) => !!e.partId && !!e.partNumber);
}
